import React, { useEffect, useMemo, useState } from "react";
import adminPosApi from "../services/adminPosApi";
import adminApiClient from "../services/adminApiClient";

export default function AdminPosTerminalsPage() {
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    branchId: "",
    name: "",
    status: "active",
  });

  const branchOptions = useMemo(() => branches || [], [branches]);

  async function loadBranches() {
    // Reuse existing branches endpoint directly to avoid coupling
    const res = await adminApiClient.get("/api/inventory/branches", { params: { limit: 200, offset: 0 } });
    if (!res.data?.ok) throw new Error("branches_load_failed");
    setBranches(res.data.branches || []);
  }

  async function loadTerminals() {
    setLoading(true);
    setStatus({ type: "", msg: "" });
    try {
      const params = { limit: 100, offset: 0 };
      if (branchId) params.branchId = Number(branchId);
      const res = await adminPosApi.listTerminals(params);
      if (!res.ok) throw new Error(res.error || "load_failed");
      setTerminals(res.terminals || []);
    } catch (e) {
      setStatus({ type: "error", msg: "Unable to load terminals." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBranches().catch(() => setStatus({ type: "error", msg: "Unable to load branches." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  async function onCreateTerminal(e) {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    const payload = {
      branchId: Number(form.branchId),
      name: form.name.trim(),
      status: form.status,
    };

    if (!payload.branchId || !payload.name) {
      setStatus({ type: "error", msg: "Branch and terminal name are required." });
      return;
    }

    try {
      const res = await adminPosApi.createTerminal(payload);
      if (!res.ok) throw new Error(res.error || "create_failed");
      setStatus({ type: "success", msg: "Terminal created." });
      setForm({ branchId: "", name: "", status: "active" });
      await loadTerminals();
    } catch (e) {
      setStatus({ type: "error", msg: "Unable to create terminal." });
    }
  }

  return (
    <div className="ucx-admin-page">
      <div className="ucx-admin-header">
        <div>
          <h1 className="ucx-admin-title">POS Terminals</h1>
          <div className="ucx-admin-subtitle">Create and manage branch terminals</div>
        </div>
      </div>

      <div className="ucx-admin-panel">
        <div className="ucx-admin-panel-title">Filter</div>
        <div className="ucx-admin-form-grid ucx-admin-form-grid-2">
          <div className="ucx-admin-field">
            <label>Branch</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branchOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} (#{b.id})
                </option>
              ))}
            </select>
          </div>
          <div className="ucx-admin-field">
            <label>Status</label>
            <input value={loading ? "Loading..." : "Ready"} readOnly />
          </div>
        </div>

        {status.type === "error" ? <div className="ucx-admin-status-error">{status.msg}</div> : null}
        {status.type === "success" ? <div className="ucx-admin-status-success">{status.msg}</div> : null}
      </div>

      <div className="ucx-admin-panel">
        <div className="ucx-admin-panel-title">Create Terminal</div>
        <form className="ucx-admin-form" onSubmit={onCreateTerminal}>
          <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
            <div className="ucx-admin-field">
              <label>Branch</label>
              <select
                value={form.branchId}
                onChange={(e) => setForm((s) => ({ ...s, branchId: e.target.value }))}
              >
                <option value="">Select branch</option>
                {branchOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} (#{b.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="ucx-admin-field">
              <label>Terminal Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Front Counter"
              />
            </div>

            <div className="ucx-admin-field">
              <label>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
          </div>

          <div className="ucx-admin-form-actions">
            <button className="ucx-btn" type="submit">
              Create
            </button>
          </div>
        </form>
      </div>

      <div className="ucx-admin-table-wrapper">
        <table className="ucx-admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Branch</th>
              <th>Name</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {terminals.length === 0 ? (
              <tr>
                <td colSpan={5} className="ucx-admin-empty">
                  No terminals found.
                </td>
              </tr>
            ) : (
              terminals.map((t) => (
                <tr key={t.id}>
                  <td>{t.id}</td>
                  <td>{t.branch_id}</td>
                  <td>{t.name}</td>
                  <td>{t.status}</td>
                  <td>{t.created_at ? new Date(t.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
